import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { ApiError } from "@/lib/apiClient";
import { useMe, useChangeOwnPassword } from "@/lib/account";
import { useFeatures, featureOf, visibilityOf } from "@/lib/license";
import { FeatureNotice } from "@/components/app/FeatureNotice";
import { roleLabel } from "@/lib/users";

const inputCls =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object") return body.message ?? body.error ?? e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/** Účet přihlášeného uživatele — profil + změna vlastního hesla. */
export function AccountPage() {
  const { t } = useTranslation();
  const me = useMe();
  const features = useFeatures();
  // Viditelnost User Managementu řídí, zda je změna hesla povolená (mirror MeWebController:
  // passwordChangeAllowed = visibility == ENABLED; LOCKED/ADVERTISED = vysvětlení proč).
  const umVisibility = visibilityOf(features.data, "admin.user-management");
  const umFeature = featureOf(features.data, "admin.user-management");
  const passwordChangeAllowed = umVisibility === "ENABLED";
  const change = useChangeOwnPassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const submit = async () => {
    setErr(null);
    setOk(false);
    if (next !== confirm) { setErr(t("account.mismatch")); return; }
    if (next.length < 12) { setErr(t("account.tooShort")); return; }
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      setOk(true);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{t("account.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("account.subtitle")}</p>
      </div>

      {me.isLoading && <LoadingIndicator />}
      {me.data && (
        <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
          <dl className="text-sm">
            <Row label={t("account.username")}>{me.data.username}</Row>
            <Row label={t("account.displayName")}>{me.data.displayName ?? "—"}</Row>
            <Row label={t("account.email")}>{me.data.email ?? "—"}</Row>
            <Row label={t("account.roles")}>
              <span className="flex flex-wrap gap-1.5">
                {me.data.roles.map((r) => <Badge key={r} type="pill-color" size="sm" color="gray">{roleLabel(r)}</Badge>)}
              </span>
            </Row>
            <Row label={t("account.authSource")}>
              {me.data.authSource ?? "LOCAL"}
              {me.data.externalIdentifier ? <span className="ml-2 font-mono text-xs text-tertiary">{me.data.externalIdentifier}</span> : null}
            </Row>
            <Row label={t("account.bootstrap")}>
              {me.data.bootstrap ? <Badge type="pill-color" size="sm" color="warning">{t("common.yes")}</Badge> : t("common.no")}
            </Row>
            <Row label={t("account.mustChange")}>
              {me.data.mustChangePassword ? <Badge type="pill-color" size="sm" color="warning">{t("common.yes")}</Badge> : t("common.no")}
            </Row>
          </dl>
        </div>
      )}

      {me.data && (() => {
        const isLocal = (me.data.authSource ?? "LOCAL") === "LOCAL";
        if (!isLocal) {
          return <div className="rounded-xl bg-primary p-5 text-sm text-tertiary ring-1 ring-secondary">{t("account.ldapNote")}</div>;
        }
        if (passwordChangeAllowed) {
          return (
            <div className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
              <h2 className="mb-4 text-sm font-semibold text-secondary">{t("account.changePassword")}</h2>
              <div className="flex max-w-md flex-col gap-3">
                <Field label={t("account.current")}><input type="password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
                <Field label={t("account.new")}><input type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} /></Field>
                <Field label={t("account.confirm")}><input type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
                {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
                {ok && <div className="rounded-lg px-3 py-2 text-sm text-success-primary ring-1 ring-secondary">{t("account.changed")}</div>}
                <div>
                  <Button size="sm" color="primary" isDisabled={!current || !next || change.isPending} onPress={() => void submit()}>
                    {t("account.save")}
                  </Button>
                </div>
              </div>
            </div>
          );
        }
        if (umVisibility === "ADVERTISED" || umVisibility === "LOCKED") {
          return (
            <FeatureNotice
              visibility={umVisibility}
              featureId="admin.user-management"
              featureName={umFeature?.name}
              advertisingText={umFeature?.advertisingText}
              plannedVersion={umFeature?.plannedVersion}
            >
              {/* Bez správy uživatelů zbývá jediná cesta ke změně hesla — rotace
                  bootstrap účtu přes proměnné prostředí. */}
              {umVisibility === "LOCKED" &&
                (me.data.bootstrap ? (
                  <div>
                    <p className="text-sm text-tertiary">{t("account.locked.bootstrapIntro")}</p>
                    <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-tertiary">
                      <li>{t("account.locked.bootstrapStep1")}</li>
                      <li>{t("account.locked.bootstrapStep2")}</li>
                      <li>{t("account.locked.bootstrapStep3")}</li>
                    </ol>
                  </div>
                ) : (
                  <p className="text-sm text-tertiary">{t("account.locked.nonbootstrap")}</p>
                ))}
            </FeatureNotice>
          );
        }
        return null;
      })()}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-secondary py-2 sm:grid-cols-[200px_1fr] sm:gap-4">
      <dt className="text-tertiary">{label}</dt>
      <dd className="text-primary">{children}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{label}</span>
      {children}
    </label>
  );
}
